import * as React from 'react';
import './confirmation-modal.scss';
import { Button, Modal, Skittles, Icon } from '@teamsnap/teamsnap-ui';
import { APP_URL, TEAMSNAP_URL } from 'core';
import { SeasonData } from '../season-guide/season-guide';

export enum ConfirmationType {
  PROGRAM,
  SEASON_CREATED,
  SEASON_FAIL,
}

type Props = {
  title: string;
  show: boolean;
  onClose: () => any;
  onSubmit?: (continueToSeason: boolean) => any;
  confirmationType: ConfirmationType;
  season?: SeasonData;
  redirect?: string;
};

const CircleWithText = ({ text, checked }: { text: string; checked?: boolean }) => (
  <div className="u-flex">
    {checked ? <img alt="checked" src={`${APP_URL}/images/task_alt.svg`} /> : <div className="circle"></div>}
    <p className="completion-breadcrumb-text u-spaceLeftSm">{text}</p>
  </div>
);

const ConfirmationModal = ({ title, show, onClose, onSubmit, confirmationType, season, redirect }: Props) => (
  <div id="program-guide-confirmation-modal">
    <Modal heading="" fullscreen show={show} showClose allowOverlayClose closeFn={onClose}>
      <div className="confirmation-modal-container">
        <div>
          {confirmationType === ConfirmationType.PROGRAM ? (
            <img className="sports-image" alt="sports" src={`${APP_URL}/images/wizard-hero.svg`} />
          ) : (
            <img className="sports-image" alt="sports" src={`${APP_URL}/images/wizard-hero-2.svg`} />
          )}
        </div>
        <div className="content-container">
          <header className="confirmation-header">
            <h4 className="program-name">
              <Skittles text={title} />
              {title}
            </h4>
            <button type="button" onClick={onClose}>
              <Icon name="dismiss" mods="u-fontSizeLg" />
            </button>
          </header>
          <article className="content">
            <div className="u-spaceEndsLg u-padTopLg">
              {confirmationType == ConfirmationType.PROGRAM && (
                <>
                  <h2>You are making great progress!</h2>
                  <p>
                    Your program information is complete! You can save your program details and continue on to create
                    your first season within the program, or save your program now and come back later to create your
                    seasons and divisions.
                  </p>
                </>
              )}
              {confirmationType == ConfirmationType.SEASON_CREATED && (
                <>
                  <h2>Congratulations!</h2>
                  <p>
                    You have successfully set up your program and season structure! TeamSnap will take it from here to
                    auto-create all of your divisions. Depending on how many divisions you selected, this may take a
                    minute or two. While we finish creating your divisions, let's get the season started by{' '}
                    <a href="https://helpme.teamsnap.com/article/1309-create-a-registration-form" target="_blank" rel="noreferrer">
                      setting up your registration
                      <Icon name="info" mods="u-spaceLeftXs u-colorPrimary6 u-padNone u-borderNone" />
                    </a>{' '}
                    .
                  </p>
                </>
              )}
              {confirmationType == ConfirmationType.SEASON_FAIL && (
                <>
                  <h2 className="u-colorNegative6">Oh no! Something went wrong</h2>
                  <p>
                    We are very sorry, but it looks like something went wrong when trying to create your season. Please
                    close the Guided Setup and try to create your season once again or reach out to TeamSnap support for
                    help.
                  </p>
                </>
              )}
            </div>

            {confirmationType !== ConfirmationType.SEASON_FAIL && (
              <>
                <div className="u-flex u-flexAlignItemsCenter">
                  <div className="completion-breadcrumb">
                    <CircleWithText checked text="Basic Program Info" />
                    <div className="line"></div>
                    <CircleWithText
                      checked={confirmationType == ConfirmationType.SEASON_CREATED || false}
                      text="Season/Division Setup"
                    />
                    <div className="line"></div>
                    <CircleWithText
                      checked={confirmationType == ConfirmationType.SEASON_CREATED || false}
                      text="Completion"
                    />
                  </div>
                  {confirmationType === ConfirmationType.SEASON_CREATED && season?.divisions?.length > 0 && (
                    <div className="u-flexGrow1">
                      <div className="created-seaons-list__container">
                        <h3>{season.name}</h3>
                        <div className="created-seaons-list">
                          {season.divisions.map((division) => (
                            <div className="created-list-item">{division.name}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="confirmation-buttons-wrapper">
                  <Button
                    size="large"
                    onClick={() => onSubmit(false)}
                    mods="save-button u-flexGrow1 u-spaceRightMd"
                    icon="right"
                    iconPosition="right"
                  >
                    {confirmationType === ConfirmationType.PROGRAM ? (
                      <span>Save and Exit</span>
                    ) : (
                      <span>Exit to Program</span>
                    )}
                  </Button>
                  {!redirect && (
                    <Button
                      mods="confirmation-button u-flexGrow1 u-spaceLeftMd"
                      color="primary"
                      icon="right"
                      iconPosition="right"
                      size="large"
                      onClick={() => onSubmit(true)}
                    >
                      {confirmationType === ConfirmationType.PROGRAM ? (
                        <span>Season/Division Setup</span>
                      ) : (
                        <span>Registration Setup</span>
                      )}
                    </Button>
                  )}
                  {redirect && (
                    <a
                      className="Button Button--large Button--primary confirmation-button u-flexGrow1 u-spaceLeftMd u-textDecorationNone"
                      href={redirect}
                    >
                      <span>
                        <span>Registration Setup</span>
                        <Icon name="right" />
                      </span>
                    </a>
                  )}
                </div>
              </>
            )}
          </article>
        </div>
      </div>
    </Modal>
  </div>
);

export default ConfirmationModal;
